import React, { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Box,
  Grid,
  InputLabel,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  Select,
  MenuItem,
  Divider,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Pagination,
  Checkbox,
  Avatar,
  Stack,
  IconButton,
  LinearProgress,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
} from '@mui/material';
import PageContainer from 'src/components/container/PageContainer';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import CheckboxInputs from 'src/components/inputs/CheckboxInputs';
import Cfilter from './Cfilter';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { useMain } from '../hooks/useMain';
import { maskData, TrimData } from './FieldData';
import ClientAnalysis from './components/ClientAnalysis';
import TemplateSelectionDialog from '../Billings/Templates/TemplateSelectionDialog';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SearchImg from '../../assets/search.svg';
import {
  Person,
  Search,
  Clear,
  Phone,
  Wc,
  Event,
  Group,
  CurrencyRupee,
  TrendingUp,
  Close as CloseIcon,
  Info,
  SentimentVerySatisfiedRounded,
  EastRounded,
  Whatshot,
  Thermostat,
  AcUnit,
  Bedtime as SnoozeIcon,
} from '@mui/icons-material';

const getUrgencyColor = (label) => {
  const category = label?.split(' ')[0]; // Strip emojis
  switch (category) {
    case 'Hot': return '#00c853'; 
    case 'Warm': return '#00acc1'; 
    case 'Cool': return '#fb8c00'; 
    case 'Cold': return '#e53935'; 
    case 'Dormant': return '#8e0000';
    default: return '#2979ff';
  }
};

const getUrgencyIcon = (label) => {
  const category = label?.split(' ')[0]; // Strip emojis
  switch (category) {
    case 'Hot': return <Whatshot fontSize="small" />; 
    case 'Warm': return <Thermostat fontSize="small" />; 
    case 'Cool': return <AcUnit fontSize="small" />; 
    case 'Cold': return <AcUnit fontSize="small" />; 
    case 'Dormant': return <SnoozeIcon fontSize="small" />;
    default: return <Info fontSize="small" />;
  }
};

const DayWiseFollowUp = () => {
  const { fetchDayWiseReport, activeBranchId, branchData, sendBulkWhatsApp, getWhatsAppTemplates, setShowMessage } = useMain();
  
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    totalCollection: 0,
    totalClients: 0,
    totalEntries: 0,
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [checkedClients, setCheckedClients] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    gender: [],
    leadStatus: [],
    services: [],
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters.search]);

  // WhatsApp State
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendError, setSendError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const filterOptions = [
    {
      title: 'Gender',
      items: [
        { value: 'Female', label: 'Female' },
        { value: 'Male', label: 'Male' },
      ]
    },
    {
      title: 'Lead Urgency',
      items: [
        { value: 'Hot', label: 'Hot Lead 🔥' },
        { value: 'Warm', label: 'Warm Lead 🌡️' },
        { value: 'Cool', label: 'Cool Lead ❄️' },
        { value: 'Cold', label: 'Cold Lead 🥶' },
        { value: 'Dormant', label: 'Dormant 💤' },
      ]
    }
  ];

  const getReport = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      setLoading(true);
      const formattedDate = selectedDate.replaceAll('-', '/');
      
      const queryParams = new URLSearchParams({
        date: formattedDate,
        page: pagination.page,
        limit: pagination.limit,
        ...(debouncedSearch && { searchValue: debouncedSearch.trim() }),
        ...(filters.gender.length > 0 && { gender: filters.gender.join(',') }),
        ...(filters.leadStatus.length > 0 && { leadStatus: filters.leadStatus.join(',') }),
        ...(filters.services.length > 0 && { services: filters.services.join(',') }),
      });

      const res = await fetchDayWiseReport(activeBranchId, `?${queryParams.toString()}`);
      
      if (res.statusCode === 200) {
        // Adapt to paginated response structure
        const apiData = res.data?.data || res.data || [];
        setReportData(apiData);
        setPagination(prev => ({
          ...prev,
          totalCount: res.data?.totalCount || 0,
          totalPages: res.data?.totalPages || 0,
          totalCollection: res.data?.totalCollection || 0,
          totalClients: res.data?.totalClients || 0,
          totalEntries: res.data?.totalEntries || 0,
        }));
      } else {
        console.error("Failed to fetch day wise report:", res.message);
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching day wise report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, selectedDate, pagination.page, pagination.limit, filters.gender, filters.leadStatus, filters.services, debouncedSearch, fetchDayWiseReport]);

  const formatToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  
  const filteredData = reportData.filter((item) => {
    // Search is now handled by backend
    const status = item.analysis?.leadLabel?.split(' ')[0]; // Normalize: "Hot 🔥" -> "Hot"

    const isLeadStatusMatch = filters.leadStatus.length === 0 || 
                             filters.leadStatus.includes(status);

    return isLeadStatusMatch;
  });

  // Sync "Select All" state with current filtered data
  useEffect(() => {
    setIsAllChecked(filteredData.length > 0 && checkedClients.length === filteredData.length);
  }, [checkedClients, filteredData]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleLimitChange = (e) => {
    setPagination(prev => ({
      ...prev,
      limit: Number(e.target.value),
      page: 1,
    }));
  };

  const handleAllCheckboxChange = () => {
    if (isAllChecked) {
      setCheckedClients([]);
    } else {
      setCheckedClients(filteredData);
    }
  };

  const handleSingleCheckboxChange = (clientId) => {
    if (checkedClients.some(check => check._id === clientId)) {
      setCheckedClients(checkedClients.filter(check => check._id !== clientId));
      setIsAllChecked(false);
    } else {
      const checked = filteredData.find(item => item._id === clientId);
      setCheckedClients([...checkedClients, checked]);
    }
  };

  // WhatsApp Handlers
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const validatePhoneNumbers = (items) => {
    const invalidNumbers = items.filter(item => {
      const number = item.client?.number?.toString().replace(/\D/g, '');
      return !number || number.length < 10 || number.length > 15;
    });

    if (invalidNumbers.length > 0) {
      return `Invalid phone numbers found for: ${invalidNumbers.map(i => i.client?.name || 'Unknown').join(', ')}`;
    }
    return null;
  };

  const prepareWhatsAppPayload = (template, items) => {
    const numbers = items.map((item) => {
      const number = item.client?.number?.toString().replace(/\D/g, '');
      return number;
    });

    const payload = {
      numbers: numbers,
      template_name: template.name,
    };

    const bodyVariables = {};
    const buttonVariables = {};

    template.variables.forEach((variable, index) => {
      // Skip header variables — they are handled via file upload
      const varType = template.variableTypes?.[variable];
      if (varType && varType.type !== 'text') return;

      const varKey = `var_${index + 1}`;
      const value = template.variableValues?.[varKey] || '';

      if (variable.toLowerCase().includes('button')) {
        const buttonIndex = variable.split('_')[1] || (index + 1);
        buttonVariables[`button${buttonIndex}`] = value;
      } else {
        const bodyIndex = variable.split('_')[1] || (index + 1);
        bodyVariables[`var${bodyIndex}`] = value;
      }
    });

    Object.assign(payload, bodyVariables, buttonVariables);

    // Include headerFile if present
    if (template.headerFile) {
      payload.headerFile = template.headerFile;
    }

    return payload;
  };

  const handleSendBulkWhatsApp = async () => {
    try {
      if (!selectedTemplate) throw new Error('Please select a WhatsApp template');
      if (checkedClients.length === 0) throw new Error('No clients selected');

      const phoneValidationError = validatePhoneNumbers(checkedClients);
      if (phoneValidationError) throw new Error(phoneValidationError);

      const payload = prepareWhatsAppPayload(selectedTemplate, checkedClients);
      
      setIsSending(true);
      setSendProgress(0);
      setSendError(null);

      const progressInterval = setInterval(() => {
        setSendProgress(prev => (prev >= 90 ? prev : prev + 10));
      }, 300);

      const res = await sendBulkWhatsApp(payload, activeBranchId);
      clearInterval(progressInterval);
      setSendProgress(100);

      if (res.statusCode === 200) {
        showSnackbar(`Messages sent successfully to ${checkedClients.length} clients`, 'success');
        setTimeout(() => {
          setCheckedClients([]);
          setOpenConfirmationDialog(false);
          setOpenWhatsAppDialog(false);
          setSelectedTemplate(null);
          setIsSending(false);
          setSendProgress(0);
        }, 2000);
      } else {
        throw new Error(res.message || 'Failed to send messages');
      }
    } catch (error) {
      setSendError(error.message);
      showSnackbar(error.message, 'error');
      setIsSending(false);
    }
  };

  const handleTemplateConfirm = (templateWithVariables) => {
    const missingVariables = templateWithVariables.variables.filter((variable, index) => {
      // Skip header variables — they use file upload
      const varType = templateWithVariables.variableTypes?.[variable];
      if (varType && varType.type !== 'text') return false;
      const varKey = `var_${index + 1}`;
      return !templateWithVariables.variableValues?.[varKey]?.trim();
    });

    if (missingVariables.length > 0) {
      setValidationError(`Please fill all required variables: ${missingVariables.join(', ')}`);
      return;
    }

    setSelectedTemplate(templateWithVariables);
    setValidationError('');
    setOpenConfirmationDialog(true);
  };

  const fetchWhatsAppTemplates = async () => {
    try {
      setLoading(true);
      const res = await getWhatsAppTemplates();
      const templatesData = res?.templates?.data || res?.data;

      if (templatesData && Array.isArray(templatesData)) {
        const approvedTemplates = templatesData.filter(template => 
          // template.languages?.[0]?.status === "APPROVED"
                      template.languages[0].status?.toUpperCase() === "APPROVED"
        );
        setTemplates(approvedTemplates);
        setOpenWhatsAppDialog(true);
      } else {
        setShowMessage({
          message: res.message || 'Failed to fetch templates',
          messageType: 'error'
        });
      }
    } catch (error) {
      setShowMessage({ message: 'Failed to fetch templates', messageType: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualClearSelection = () => {
    setCheckedClients([]);
    setIsAllChecked(false);
  };

  const isSalesMasked = branchData?.isMasked || false;

  const maskValue = (value) => {
    if (isSalesMasked && value !== undefined && value !== null) {
      const valueStr = value.toString();
      const maskLength = Math.ceil(valueStr.length);
      return '*'.repeat(maskLength) + valueStr.substring(maskLength);
    } else {
      return value;
    }
  };
   const followUpGridTemplate = '50px 180px 140px 110px 1fr 110px 100px 80px';
  const followUpGridRowStyle = {
    display: 'grid',
    gridTemplateColumns: followUpGridTemplate,
    alignItems: 'center',
    columnGap: '6px',
  };


  const statsData = [
    {
      no: `INR ${maskValue(pagination.totalCollection.toLocaleString())}`,
      label: 'TOTAL COLLECTION',
      color: '#FF5E1B',
      icon: <CurrencyRupee sx={{ color: '#FF5E1B' }} />,
    },
    {
      no: pagination.totalClients,
      label: 'TOTAL CLIENTS',
      color: '#03C4A9',
      icon: <Person sx={{ color: '#03C4A9' }} />,
    },
    {
      no: pagination.totalEntries,
      label: 'TOTAL ENTRIES',
      color: '#3071FE',
      icon: <Group sx={{ color: '#3071FE' }} />,
    },
    
  ];

  // Remove only leading spaces while typing (spaces between words still work)
  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value.trimStart() }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Cleans pasted text, then inserts it at the cursor position
  const handleSearchPaste = (e) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text').trim();

    // If it looks like a phone number, remove spaces, dashes, brackets and +91
    if (/^[+\d\s\-()]+$/.test(pasted)) {
      pasted = pasted.replace(/[\s\-()]/g, '').replace(/^\+?91(?=\d{10}$)/, '');
    }

    const { selectionStart, selectionEnd, value } = e.target;
    const next = (value.slice(0, selectionStart) + pasted + value.slice(selectionEnd)).trim();
    setFilters((prev) => ({ ...prev, search: next }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Trims trailing spaces when the user leaves the box
  const handleSearchBlur = (e) => {
    const trimmed = e.target.value.trim();
    if (trimmed !== e.target.value) {
      setFilters((prev) => ({ ...prev, search: trimmed }));
    }
  };

  return (
    <PageContainer title="Client Follow Up" description="Follow-up report for clients">
      <DashboardCard2 >
        {loading ? (
          <ShowLoader />
        ) : (
          <>
            {/* Filter & Search Bar Section */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-end', 
              mb: 3 
            }}>
              {/* Left side - Show per page */}
              <Box>
                <InputLabel>Show per page</InputLabel>
                <Select
                  style={{
                    borderRadius: '7px',
                    border: '0.5px solid #6174DD',
                    padding: '4px 12px',
                    background: '#FFF',
                    height: '40px',
                    width: '100px',
                  }}
                  value={pagination.limit}
                  onChange={handleLimitChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={1000000}>All</MenuItem>
                </Select>
              </Box>

            {/* Right side - Search and Filter */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <div style={{ width: '170px', flexShrink: 0 }} onClick={() => document.getElementById('search-input').focus()}>
                    <InputLabel>Search</InputLabel>
                  <div
                    className="search-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '0.5px solid #6174DD',
                      borderRadius: '7px',
                      padding: '4px 12px',
                      background: '#FFF',
                      height: '40px',
                      width: '100%',
                      maxWidth: 'none',
                      margin: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                  <img src={SearchImg} alt="" style={{ marginRight: '8px' }} />
                  <input
                    id="search-input"
                    type="text"
                    placeholder="Search clients..."
                    value={filters.search || ''}
                    onChange={handleSearchChange}
                    onPaste={handleSearchPaste}
                    onBlur={handleSearchBlur}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      marginLeft: '0.5rem',
                    }}
                  />
                  {filters.search && (
                    <IconButton
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, search: '' }));
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      style={{ padding: 1 }}
                    >
                      <Clear
                        style={{
                          color: '#000',
                          marginRight: '-0.2rem',
                          marginTop: '0.1rem',
                          fontSize: '1.1rem',
                        }}
                      />
                    </IconButton>
                  )}
                </div>
              </div>

                <Box sx={{ width: '150px', flexShrink: 0, ml: 0 }}>
                  <Cfilter
                    placeholder="Filter Status"
                    options={filterOptions}
                    value={[...filters.gender, ...filters.leadStatus]}
                    onChange={(values) => {
                      const gender = values.filter(v => ['Male', 'Female'].includes(v));
                      const leadStatus = values.filter(v => ['Hot', 'Warm', 'Cool', 'Cold', 'Dormant'].includes(v));
                      setFilters({ ...filters, gender, leadStatus });
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Summary Statistics Cards */}
            {pagination.totalCount > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {statsData.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={3}
                      style={{
                        backgroundColor: item.color,
                        textAlign: 'left',
                        height: '150px',
                        padding: '20px 20px',
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        borderRadius: '10px',
                        gap: '10px',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        width={'100%'}
                        color={'white'}
                        alignItems={'flex-start'}
                        justifyContent={'space-between'}
                      >
                        <IconButton
                          sx={{
                            background: 'white',
                            '&:hover': { background: 'white' },
                          }}
                        >
                          {item.icon}
                        </IconButton>
                        {/* <IconButton 
                          sx={{ 
                            color: 'white', 
                            '&:hover': { color: `${item.color}`, background: 'white' } 
                          }}
                        >
                          <EastRounded />
                        </IconButton> */}
                      </Stack>
                      <div style={{ color: '#fff', fontWeight: '900', fontSize: '25px' }}>{item.no}</div>
                      <Box>
                        <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{item.label}</div>
                        <div style={{ color: '#fff', fontWeight: '500', fontSize: '10.5px', opacity: 0.85, marginTop: '-3px' }}>
                          (Data of last three months and one week)
                        </div>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            <Box sx={{ overflow: 'auto', width: '100%' }}>
              <table className="table-container mt-3" style={{ width: '100%', minWidth: '1050px', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                <thead className="table-thead">
                  <tr style={followUpGridRowStyle}>
                    <th style={{ textAlign: 'center' }}>
                      <CheckboxInputs type={'Heading'} isAllChecked={isAllChecked} handleAllCheckboxChange={handleAllCheckboxChange} />
                    </th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Client Name</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Number</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><span>Lead Label</span></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Last Service Name</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Date of Last Bill</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Gender</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Details</span></Stack></th>
                  </tr>
                </thead>
                <tbody className="table-tbody">
                  {filteredData.length > 0 ? (() => {
                    // Group items by month and year
                    const grouped = filteredData.reduce((acc, item) => {
                      const date = new Date(item.dateOfBilling);
                      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(item);
                      return acc;
                    }, {});

                    return Object.keys(grouped).map((monthYear) => (
                      <Fragment key={monthYear}>
                        <tr style={{ backgroundColor: 'transparent', boxShadow: 'none', border: 'none', display: 'block' }}>
                          <td style={{ padding: '10px 0 5px 0px', textAlign: 'left', border: 'none', boxShadow: 'none', display: 'block' }}>
                            <Box sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 1.5, 
                              pl: 2,
                              pr: 4,
                              py: 0.8,
                              background: 'linear-gradient(90deg, #1a237e 0%, #3f51b5 100%)',
                              color: 'white',
                              borderRadius: '0 50px 50px 0',
                              boxShadow: '2px 4px 8px rgba(26, 35, 126, 0.3)',
                            }}>
                              <Event sx={{ fontSize: '1.1rem' }} />
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                                {monthYear}
                              </Typography>
                            </Box>
                          </td>
                        </tr>
                        {grouped[monthYear].map((item) => {
                          const label = item.analysis?.leadLabel?.split(' ')[0]; // Normalize

                          let rowBg = '#e3f2fd'; 
                          let accentColor = '#2979ff'; // Default Blue

                          if (label === 'Hot') { 
                            rowBg = '#e8f5e9'; 
                            accentColor = '#00c853'; // Vibrant green
                          } else if (label === 'Warm') { 
                            rowBg = '#e0f7fa'; 
                            accentColor = '#00acc1'; // Cyan
                          } else if (label === 'Cool') { 
                            rowBg = '#fff3e0'; 
                            accentColor = '#fb8c00'; // Orange
                          } else if (label === 'Cold') { 
                            rowBg = '#fff5f5'; 
                            accentColor = '#e53935'; // Lite Red
                          } else if (label === 'Dormant') { 
                            rowBg = '#fbe9e7'; 
                            accentColor = '#8e0000'; // Dark Red
                          }

                          return (
                            <tr 
                              key={item._id} 
                              style={{ 
                                ...followUpGridRowStyle,
                                transition: 'all 0.2s ease',
                                cursor: 'default'
                              }}
                            >
                              <td style={{ textAlign: 'center', padding: '4px 0 4px 15px' }}>
                                <CheckboxInputs type={'content'} handleSignleCheckboxChange={() => handleSingleCheckboxChange(item._id)} checkedItems={checkedClients} fieldId={item?._id} />
                              </td>
                              <td style={{ 
                                textAlign: 'center', fontWeight: '600', 
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', 
                                padding: '4px 8px', color: '#1a237e'
                              }}>
                                {TrimData(item.client?.name)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                <Typography variant="body2" sx={{ color: '#444', fontSize: '0.8rem', fontWeight: 500 }}>
                                  {typeof branchData?.isMasked !== 'boolean' || !item.client?.number
                                    ? 'loading...'
                                    : maskData(item.client?.number, branchData?.isMasked)}
                                </Typography>
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                <Chip 
                                  label={item.analysis?.leadLabel || 'Standard'} 
                                  size="small" 
                                  sx={{ fontWeight: '800', fontSize: '0.7rem', height: '24px', bgcolor: rowBg, color: accentColor, border: `1px solid ${accentColor}` }} 
                                />
                              </td>
                              <td style={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px 10px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }} title={item.services?.map(s => s.serviceName).join(', ')}>
                                  {item.services?.map(s => s.serviceName).join(', ') || '-'}
                                </Typography>
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 8px', fontSize: '0.8rem' }}>{formatToDDMMYYYY(item.dateOfBilling)}</td>
                              <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                <Chip 
                                  label={item.client?.gender} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ fontSize: '9px', height: '18px', fontWeight: '800', textTransform: 'uppercase', borderColor: '#ccc' }} 
                                />
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 8px', borderRadius: '0 6px 6px 0' }}>
                                <IconButton onClick={() => handleOpenDetails(item)} color="primary" size="small" sx={{ p: '2px' }}>
                                  <Info fontSize="small" />
                                </IconButton>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ));
                  })() : (
                    <tr style={{ display: 'block' }}>
                      <td style={{ textAlign: 'center', padding: '50px', display: 'block' }}>
                        <Typography color="textSecondary" variant="h6">No records found for this period.</Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                color="primary"
                onChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                disabled={pagination.totalPages <= 1}
              />
            </Box>

            {/* WhatsApp Selection Card */}
            {checkedClients.length > 0 && (
              <Card sx={{ mt: 4, mb: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '12px' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" sx={{ color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsAppIcon color="success" /> Ready to Send Follow-Up
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center" mt={1}>
                        <Chip label={`${checkedClients.length} leads selected`} color="primary" variant="outlined" size="small" />
                        <Button variant="text" color="secondary" onClick={handleManualClearSelection} size="small">Clear Selection</Button>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      onClick={fetchWhatsAppTemplates}
                      sx={{
                        background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                        borderRadius: '8px',
                        px: 3,
                        '&:hover': { background: 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)' }
                      }}
                    >
                      Send Bulk WhatsApp
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* WhatsApp Dialogs */}
            <TemplateSelectionDialog
              open={openWhatsAppDialog}
              templates={templates}
              onClose={() => {
                if (!isSending) {
                  setOpenWhatsAppDialog(false);
                  setSelectedTemplate(null);
                  setSendError(null);
                  setValidationError('');
                }
              }}
              onConfirm={handleTemplateConfirm}
              isSending={isSending}
              sendProgress={sendProgress}
              sendError={sendError}
              validationError={validationError}
              setSendError={setSendError}
            />

            <Dialog open={openConfirmationDialog} onClose={() => !isSending && setOpenConfirmationDialog(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ textAlign: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <WhatsAppIcon color="success" />
                  <Typography variant="h6">{isSending ? `Sending... ${sendProgress}%` : 'Confirm Follow-Up'}</Typography>
                </Box>
                {isSending && <LinearProgress variant="determinate" value={sendProgress} sx={{ mt: 1 }} />}
              </DialogTitle>
              <DialogContent>
                {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}
                <Alert severity="info" sx={{ mb: 2 }}>Sending WhatsApp to {checkedClients.length} clients from your follow-up list.</Alert>
                {selectedTemplate && (
                  <Box>
                    <Typography variant="subtitle2">Template: <strong>{selectedTemplate.name}</strong></Typography>
                    <Box mt={2}>
                      <Typography variant="caption" color="textSecondary">Preview:</Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', mt: 0.5 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTemplate.bodyText}</Typography>
                      </Paper>
                    </Box>
                    {selectedTemplate.variables.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">Variables:</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                          {selectedTemplate.variables.map((variable, index) => (
                            <Chip
                              key={variable}
                              label={`${variable}: ${selectedTemplate.variableValues?.[`var_${index + 1}`] || 'N/A'}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setOpenConfirmationDialog(false)} disabled={isSending}>Cancel</Button>
                <Button
                  onClick={handleSendBulkWhatsApp}
                  variant="contained"
                  disabled={isSending}
                  sx={{
                    background: isSending ? '#ccc' : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                    px: 4
                  }}
                >
                  {isSending ? 'Sending...' : 'Confirm & Send'}
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
              </Alert>
            </Snackbar>

            <Dialog open={openDialog} onClose={handleCloseDetails} maxWidth="md" fullWidth>
              <DialogTitle sx={{ 
                bgcolor: selectedItem?.analysis ? `${getUrgencyColor(selectedItem.analysis.leadLabel)}08` : '#f5f7ff', 
                borderBottom: '1px solid #e0e0e0', 
                p: 2 
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Typography variant="h5" fontWeight="800" color="#1a237e">
                      Client Analysis: {selectedItem?.client?.name}
                    </Typography>
                    {selectedItem?.analysis && (
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 3, 
                        alignItems: 'center',
                        bgcolor: 'white',
                        px: 3,
                        py: 1,
                        borderRadius: '12px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#6174DD', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Lead Status
                          </Typography>
                          <Chip 
                            icon={getUrgencyIcon(selectedItem.analysis.leadLabel)}
                            label={selectedItem.analysis.leadLabel}
                            size="small"
                            sx={{ 
                              fontWeight: '800', 
                              bgcolor: getUrgencyColor(selectedItem.analysis.leadLabel), 
                              color: 'white',
                              height: '26px',
                              px: 0.5,
                              '& .MuiChip-icon': { color: 'white' }
                            }}
                          />
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ height: '30px', my: 'auto' }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#6174DD', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Lead Score
                          </Typography>
                          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress
                              variant="determinate"
                              value={100}
                              size={42}
                              thickness={5}
                              sx={{ color: '#eee' }}
                            />
                            <CircularProgress
                              variant="determinate"
                              value={selectedItem.analysis.leadScore || 0}
                              size={42}
                              thickness={5}
                              sx={{ 
                                color: getUrgencyColor(selectedItem.analysis.leadLabel),
                                position: 'absolute',
                                [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ position: 'absolute', fontWeight: '900', fontSize: '0.8rem', color: '#1a237e' }}
                            >
                              {selectedItem.analysis.leadScore}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <IconButton onClick={handleCloseDetails} size="small"><CloseIcon /></IconButton>
                </Stack>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <ClientAnalysis 
                  selectedItem={selectedItem} 
                  allData={reportData} 
                  branchData={branchData} 
                />
              </DialogContent>
              <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fcfcfc' }}>
                <Button onClick={handleCloseDetails} variant="contained" sx={{ bgcolor: '#6174DD', px: 4 }}>Close Detail</Button>
              </DialogActions>
            </Dialog>

            {/* {reportData.length > 0 && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', bgcolor: '#f8faff', border: '1px solid #e0e6ff' }}>
                    <Avatar sx={{ bgcolor: '#6174DD', width: 48, height: 48 }}><TrendingUp /></Avatar>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: '700' }}>Total Collection</Typography>
                      <Typography variant="h4" fontWeight="800" sx={{ color: '#1a237e' }}>₹{totalCollection.toLocaleString()}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', bgcolor: '#f3e5f5', border: '1px solid #e1bee7' }}>
                    <Avatar sx={{ bgcolor: '#9c27b0', width: 48, height: 48 }}><Person /></Avatar>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: '700' }}>Total Clients</Typography>
                      <Typography variant="h4" fontWeight="800" sx={{ color: '#4a148c' }}>{uniqueClientCount}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', bgcolor: '#fffcf5', border: '1px solid #ffeeba' }}>
                    <Avatar sx={{ bgcolor: '#ffa000', width: 48, height: 48 }}><Group /></Avatar>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: '700' }}>Total Entries</Typography>
                      <Typography variant="h4" fontWeight="800" sx={{ color: '#5f4b00' }}>{filteredData.length}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )} */}
          
          </>
        )}
      </DashboardCard2>
    </PageContainer>
  );
};

export default DayWiseFollowUp;
